import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { Mail } from "lucide-react";
import {
  extractPreAuthToken,
  extractTeacherRecord,
  extractTeacherToken,
  teacherApi,
} from "../../features/teacher/teacherApi";
import {
  clearTeacherSignupContext,
  setTeacherSession,
  setTeacherSignupContext,
} from "../../features/teacher/teacherSlice";
import {
  TeacherAuthField,
  TeacherAuthLayout,
  TeacherFeedbackMessage,
} from "./TeacherUI";
import { getTeacherAuthErrorMessage } from "../../utils/uiErrorMessages";

const getTeacherBackendMessage = (payload, fallbackMessage = "") =>
  String(payload?.message || payload?.detail || "").trim() || fallbackMessage;

export default function TeacherOtpLogin() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [generalMessage, setGeneralMessage] = useState("");

  const redirectPath = location.state?.from?.pathname || "/teacherdashboard";
  const entryMessage = useMemo(
    () => String(location.state?.message || "").trim(),
    [location.state]
  );

  const otpLoginMutation = useMutation({
    mutationFn: teacherApi.loginWithOtp,
    onSuccess: (payload) => {
      const normalizedEmail = String(email || "").trim();
      const teacherToken = extractTeacherToken(payload);
      const teacherRecord = extractTeacherRecord(payload, {
        email: normalizedEmail,
      });

      if (teacherToken) {
        dispatch(
          setTeacherSession({
            token: teacherToken,
            teacher: teacherRecord,
          })
        );
        dispatch(clearTeacherSignupContext());
        navigate(redirectPath, { replace: true });
        return;
      }

      const preAuthToken = extractPreAuthToken(payload);
      if (!preAuthToken) {
        setGeneralMessage(
          getTeacherBackendMessage(
            payload,
            "Unable to continue with teacher OTP login."
          )
        );
        return;
      }

      dispatch(
        setTeacherSignupContext({
          preAuthToken,
          email: normalizedEmail,
          source: "login",
        })
      );

      navigate("/teacherverify-otp", {
        replace: true,
        state: {
          preAuthToken,
          email: normalizedEmail,
          source: "login",
          redirectPath,
          message: getTeacherBackendMessage(payload, "OTP sent to your email."),
        },
      });
    },
    onError: (error) => {
      const rawPayload = error?.raw || {};
      const preAuthToken = extractPreAuthToken(rawPayload);

      if (preAuthToken) {
        const normalizedEmail = String(email || "").trim();

        dispatch(
          setTeacherSignupContext({
            preAuthToken,
            email: normalizedEmail,
            source: "login",
          })
        );

        navigate("/teacherverify-otp", {
          replace: true,
          state: {
            preAuthToken,
            email: normalizedEmail,
            source: "login",
            redirectPath,
            message: getTeacherBackendMessage(rawPayload, "OTP sent to your email."),
          },
        });
        return;
      }

      setFieldErrors(error.fieldErrors || {});
      setGeneralMessage(getTeacherAuthErrorMessage(error, "login"));
    },
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    setFieldErrors({});
    setGeneralMessage("");

    const normalizedEmail = String(email || "").trim();
    const nextErrors = {};

    if (!normalizedEmail) {
      nextErrors.email = "Email is required.";
    }

    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors);
      return;
    }

    otpLoginMutation.mutate({
      email: normalizedEmail,
    });
  };

  return (
    <TeacherAuthLayout
      title="Teacher OTP Login"
      subtitle="Enter your email to receive a one-time passcode for teacher login."
      closeTo="/teacherlogin"
      footer={
        <p className="teacher-auth-footer-text">
          Prefer your password?{" "}
          <Link className="teacher-auth-link" to="/teacherlogin">
            Back to login
          </Link>
        </p>
      }
    >
      {entryMessage ? (
        <TeacherFeedbackMessage variant="info">{entryMessage}</TeacherFeedbackMessage>
      ) : null}
      {generalMessage ? (
        <TeacherFeedbackMessage variant="error">{generalMessage}</TeacherFeedbackMessage>
      ) : null}

      <form className="teacher-auth-form" onSubmit={handleSubmit} noValidate>
        <TeacherAuthField
          id="teacher-otp-login-email"
          label="Email address"
          icon={Mail}
          name="email"
          type="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);

            if (fieldErrors.email) {
              setFieldErrors((previous) => ({
                ...previous,
                email: "",
              }));
            }

            if (generalMessage) {
              setGeneralMessage("");
            }
          }}
          autoComplete="email"
          placeholder="Enter your email"
          error={fieldErrors.email}
        />

        <div className="teacher-auth-row" style={{ justifyContent: "center" }}>
          <span className="teacher-auth-note">
            {otpLoginMutation.isPending
              ? "Sending your teacher login OTP..."
              : "We'll email a one-time passcode to complete teacher login."}
          </span>
        </div>

        <button
          className="teacher-auth-button"
          type="submit"
          disabled={otpLoginMutation.isPending}
        >
          {otpLoginMutation.isPending ? "Sending OTP..." : "Continue with OTP"}
        </button>
      </form>
    </TeacherAuthLayout>
  );
}
